import {
  annotatePricesIngredientGroup,
  productPrice,
  toCalculatable,
  toPrintable,
  COUPON_CATEGORIES,
  totalPriceRaw,
  productPriceWithoutDeposit,
  isPricingTypeSum,
  isPricingTypeFixed,
  isPricingTypeClassic
} from "./pricing";
import { createNewObject, getPopulatedIngredientGroups } from "./products";
import { isEmpty } from "lodash";

export function basketToReceipt(basket, coupons) {
  let items = [];
  basket.forEach(basketItem => {
    if (basketItem.selectedCombo) {
      items.push(...menuToReceipt(basketItem, coupons));
    } else {
      items.push(...productToReceipt(basketItem, coupons));
    }
  });
  items.push(...globalCouponToReceipt(basket, coupons));
  items.push({
    type: "TOTAL_DEPOSIT",
    totalDeposit: basket.vatDetails.depositPaid
  });
  return items;
}

function menuToReceipt(basketItem, coupons) {
  let items = [];
  let menu_price = toCalculatable(basketItem.price * basketItem.count);
  let first_product = basketItem.components[0].selected;
  items.push({
    uuid: basketItem.id,
    type: "MENU_HEAD",
    price: menu_price !== 0.0 ? toPrintable(menu_price) : "",
    count: basketItem.count,
    name: basketItem.name,
    first_product_uuid: first_product.id,
    first_product_name: first_product.name,
    taxPrice: productPriceWithoutDeposit(basketItem, true, coupons).toFixed(2),
    vats: basketItem.vats
  });
  basketItem.components.forEach((component, index) => {
    items.push(
      ...menuComponentToReceipt(component.selected, basketItem, index)
    );
  });
  return items;
}

function menuComponentPrice(basketItem, menu, component_index) {
  let result = 0.0;
  if (isPricingTypeClassic(menu)) {
    // CLASSIC: use price attribute for first item otherwise menu_offset_price
    let classic_price =
      component_index === 0
        ? toCalculatable(basketItem.price)
        : toCalculatable(basketItem.menu_offset_price);
    if (basketItem.selectedSize) {
      classic_price += toCalculatable(basketItem.selectedSize.offset_price);
    }
    result = menu.count * classic_price;
  } else if (isPricingTypeFixed(menu)) {
    // FIXED: use fixed_price attribute
    let fixed_price = basketItem.fixed_price
      ? toCalculatable(basketItem.fixed_price)
      : 0.0;
    result = menu.count * fixed_price;
  } else if (isPricingTypeSum(menu)) {
    // SUM: use price attribute
    result = menu.count * basketItem.price;
  }
  return result !== 0.0 ? toPrintable(result, true) : "";
}

function menuComponentToReceipt(basketItem, menu, component_index) {
  let items = [];
  let type = "MENU_COMPONENT";
  let price = menuComponentPrice(basketItem, menu, component_index);
  let count = menu.count;
  let name = basketItem.name;
  let master_category = basketItem.master_category;
  let master_category_name = basketItem.master_category_name;
  let scanned = basketItem.scanned;
  let no_product = basketItem.no_product;
  let menu_component_id = menu.components[component_index].id;
  let uuid = basketItem.id;
  let size_uuid = null;

  if (basketItem.selectedSize) {
    name = `${name} (${basketItem.selectedSize.size})`;
    size_uuid = basketItem.selectedSize.id;
  }

  let deposit = basketItem.deposit ? toCalculatable(basketItem.deposit) : 0.0;
  items.push({
    type,
    price,
    deposit,
    count,
    name,
    master_category,
    master_category_name,
    scanned,
    no_product,
    menu_component_id,
    uuid,
    size_uuid
  });
  items.push(...ingredientsToReceipt(basketItem, count));
  items.push(...extrasToReceipt(basketItem, count));
  items.push(...productDepositToReceipt(basketItem, count));
  return items;
}

function productToReceipt(basketItem, global_coupons) {
  let items = [];
  let price = toCalculatable(basketItem.vatDetails.grossBasePrice);
  let deposit = basketItem.deposit
    ? toCalculatable(basketItem.vatDetails.depositPaid)
    : 0.0;
  let count = basketItem.count;
  let name = basketItem.name;
  let uuid = basketItem.id;
  let master_category = basketItem.master_category;
  let master_category_name = basketItem.master_category_name;
  let size_uuid = null;

  if (basketItem.selectedSize) {
    size_uuid = basketItem.selectedSize.id;
    name = `${name} (${basketItem.selectedSize.size})`;
  }
  price = price !== 0.0 ? toPrintable(price) : "";
  deposit = deposit !== 0.0 ? toPrintable(deposit) : "";
  let taxPrice = 0.0;
  for (let i = 0; i < basketItem.count; i++) {
    taxPrice += parseFloat(
      productPriceWithoutDeposit(basketItem, true, global_coupons, i).toFixed(2)
    );
  }
  //taxPrice get multiplied later so we need to get rid of the count factor to get the average tax price:
  taxPrice /= parseFloat(basketItem.count);
  items.push({
    uuid,
    type: "PRODUCT_HEAD",
    price,
    deposit,
    count,
    name,
    master_category,
    master_category_name,
    taxPrice,
    vat: basketItem.vat,
    vats: basketItem.vats,
    scanned: basketItem.scanned,
    no_product: basketItem.no_product,
    size_uuid
  });
  items.push(...ingredientsToReceipt(basketItem, count));
  items.push(...extrasToReceipt(basketItem, count));
  items.push(...productCouponToReceipt(basketItem));
  items.push(...productDepositToReceipt(basketItem, count));
  return items;
}

function ingredientsToReceipt(basketItem, count) {
  let items = [];
  let igs = getPopulatedIngredientGroups(basketItem);
  igs.forEach(ig => {
    const groupHasSelectedItems = ig.ingredients.some(i => !!i.count);
    const groupHasDeselectedDefaultIngredient = ig.ingredients.some(
      i => i.default_count > 0 && i.count === 0
    );
    if (groupHasSelectedItems || groupHasDeselectedDefaultIngredient) {
      let name = ig.display_name || ig.name;
      if (ig.id !== -1) {
        items.push({
          uuid: ig.id,
          type: "PRODUCT_INGREDIENT_HEAD",
          name,
          price: "", //toPrintable(ingredientPrice(basketItem, ig.id)),
          count
        });
      }
      annotatePricesIngredientGroup(basketItem, ig).forEach(ing => {
        if (
          ing.count !== ing.default_count ||
          (!basketItem.default_ingredients_included && ing.count !== 0)
        ) {
          items.push({
            uuid: ing.id,
            type: "PRODUCT_INGREDIENT",
            name: ing.name,
            count: ing.count,
            price:
              ing.totalPrice !== 0.0
                ? toPrintable(count * ing.totalPrice, true)
                : ""
          });
        }
      });
    }
  });

  return items;
}

function extrasToReceipt(basketItem, count) {
  let items = [];
  if (isEmpty(basketItem.selectedExtras)) return [];
  basketItem.selectedExtras.forEach(extra => {
    let price = toCalculatable(extra.offset_price);
    items.push({
      type: "PRODUCT_EXTRA",
      uuid: extra.id,
      name: extra.name,
      product: extra.product,
      extra_category_id: extra.extra_category_id,
      price: price !== 0.0 ? toPrintable(price, true) : "",
      count
    });
  });
  return items;
}

function productCouponToReceipt(basketItem) {
  let items = [];
  if (isEmpty(basketItem.coupon)) return [];
  let coupon = createNewObject(basketItem.coupon);
  let price = 0.0;
  let name = "Coupon";
  if (coupon.data.c === COUPON_CATEGORIES.AMOUNT_OFF_CATEGORY) {
    price = -1 * coupon.data.a;
    name = "Coupon (" + toPrintable(-1 * coupon.data.a) + "€ korting)";
  }
  if (coupon.data.c === COUPON_CATEGORIES.PERCENT_OFF_CATEGORY) {
    let amount = Math.round(coupon.data.a);
    name = `Coupon (${amount}% korting)`;
    price = productPrice(basketItem, false) * (coupon.data.a / 100.0) * -1;
  }
  items.push({
    type: "PRODUCT_COUPON",
    name,
    price: price !== 0.0 ? toPrintable(price, true) : ""
  });
  return items;
}

function productDepositToReceipt(basketItem, count) {
  let items = [];
  if (isEmpty(basketItem.deposit)) return [];
  let deposit = basketItem.deposit;
  let name = "Statiegeld";

  items.push({
    type: "PRODUCT_DEPOSIT",
    name,
    price: deposit !== 0.0 ? toPrintable(count * deposit, true) : "",
    count
  });
  return items;
}

function globalCouponToReceipt(basket, coupons) {
  let items = [];
  coupons.forEach(coupon => {
    let name = "Coupon";
    if (coupon.data.c === COUPON_CATEGORIES.AMOUNT_OFF) {
      name = "Coupon (" + toPrintable(-1 * coupon.data.a) + "€ korting)";
      items.push({
        type: "COUPON",
        name,
        price: toPrintable(-1 * coupon.data.a, true),
        taxPrice: -1 * coupon.data.a,
        count: 1
      });
    }
    if (coupon.data.c === COUPON_CATEGORIES.PERCENT_OFF) {
      let amount = Math.round(coupon.data.a);
      name = `Coupon (${amount}% korting)`;
      items.push({
        type: "COUPON",
        name,
        price: toPrintable(-1 * basket.vatDetails.grossBaseDiscount, true)
      });
    }
  });
  return items;
}
